import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import { TableSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, MagnifyingGlass, Buildings, User, PencilSimple, Trash, Phone, Envelope, Globe, TreeStructure, ArrowElbowDownRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SortHeader from "../components/SortHeader";

const EMPTY_FORM = { first_name: "", last_name: "", company_name: "", job_role: "", company_address: "", company_street: "", company_town: "", company_county: "", company_postcode: "", company_lat: null, company_lng: null, email: "", phone: "", portal_enabled: false, parent_client_id: "", po_required: false };
const KIND_LABEL = { main: "Main client", sub: "Sub-client", standalone: "Standalone" };

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  // Phase 258 — sortable column headers
  const [sortKey, setSortKey] = useState("company");
  const [sortDir, setSortDir] = useState("asc");
  const [kind, setKind] = useState("all"); // all | main | sub | standalone
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortedClients = [...clients].sort((a, b) => {
    const get = (c) => {
      switch (sortKey) {
        case "primary":
          return `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase();
        case "email":
          return (c.email || "").toLowerCase();
        case "phone":
          return (c.phone || "").toLowerCase();
        case "portal":
          return c.portal_enabled ? "1" : "0";
        case "contacts":
          return (c.contacts?.length || 0).toString().padStart(6, "0");
        case "company":
        default:
          return (c.company_name || `${c.first_name || ""} ${c.last_name || ""}`).trim().toLowerCase();
      }
    };
    const av = get(a), bv = get(b);
    const cmp = av.localeCompare(bv, undefined, { numeric: true });
    return sortDir === "asc" ? cmp : -cmp;
  });
  // Nest sub-clients directly under their main client (when the parent is in the list); apply the kind filter
  const visibleClients = (() => {
    const filtered = kind === "all" ? sortedClients : sortedClients.filter(c => c.client_kind === kind);
    if (kind !== "all") return filtered;
    const byParent = {};
    filtered.forEach(c => { if (c.parent_client_id) (byParent[c.parent_client_id] = byParent[c.parent_client_id] || []).push(c); });
    const parentIds = new Set(filtered.map(c => c.id));
    const out = [];
    filtered.forEach(c => {
      if (c.parent_client_id && parentIds.has(c.parent_client_id)) return; // rendered under its parent
      out.push(c);
      (byParent[c.id] || []).forEach(sub => out.push({ ...sub, _nested: true }));
    });
    return out;
  })();
  const mainCandidates = clients.filter(c => c.client_kind !== "sub" && c.id !== editingClient?.id);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const { data } = await API.get(`/clients${params}`);
      setClients(data.clients || []);
      setTotal(data.total || 0);
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Auto-open create dialog when arriving with ?new=1
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditingClient(null);
      setForm({ ...EMPTY_FORM, parent_client_id: searchParams.get("parent") || "" });
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      next.delete("parent");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // ?kind=sub|main|standalone (sidebar → Clients › Sub Clients) pre-selects the kind filter
  const wantKind = searchParams.get("kind");
  useEffect(() => { setKind(["main", "sub", "standalone"].includes(wantKind || "") ? wantKind : "all"); }, [wantKind]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("First and last name are required"); return; }
    setSaving(true);
    try {
      if (editingClient) {
        await API.put(`/clients/${editingClient.id}`, form);
        if ((form.parent_client_id || "") !== (editingClient.parent_client_id || "")) {
          await API.put(`/clients/${editingClient.id}/parent`, { parent_client_id: form.parent_client_id || null });
        }
        toast.success("Client updated");
      } else {
        await API.post("/clients", form);
        toast.success("Client created");
      }
      setShowCreate(false);
      setEditingClient(null);
      setForm({ ...EMPTY_FORM });
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save client"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client? This cannot be undone.")) return;
    try {
      await API.delete(`/clients/${id}`);
      toast.success("Client deleted");
      fetchClients();
    } catch { toast.error("Failed to delete client"); }
  };

  const openEdit = (client) => {
    setEditingClient(client);
    setForm({ first_name: client.first_name, last_name: client.last_name, company_name: client.company_name || "", job_role: client.job_role || "", company_address: client.company_address || "", company_street: client.company_street || "", company_town: client.company_town || "", company_county: client.company_county || "", company_postcode: client.company_postcode || "", company_lat: client.company_lat || null, company_lng: client.company_lng || null, email: client.email || "", phone: client.phone || "", portal_enabled: client.portal_enabled || false, parent_client_id: client.parent_client_id || "", po_required: client.po_required || false });
    setShowCreate(true);
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} client{total !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => { setEditingClient(null); setForm({ ...EMPTY_FORM }); setShowCreate(true); }} data-testid="add-client-btn">
          <Plus size={16} className="mr-1" /> Add Client
        </Button>
      </div>

      {/* Search + kind filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="pl-9" data-testid="client-search-input" />
        </div>
        <div className="flex items-center gap-1 border border-border rounded-md p-0.5 bg-muted/20" data-testid="client-kind-filter">
          {["all", "main", "sub", "standalone"].map(k => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`px-2.5 py-1 text-xs rounded ${kind === k ? "bg-background shadow-sm font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`client-kind-${k}`}>
              {k === "all" ? "All" : KIND_LABEL[k] + "s"}{k !== "all" && <span className="ml-1 text-[10px] text-muted-foreground">{clients.filter(c => c.client_kind === k).length}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Clients Table */}
      {loading ? (
        <TableSkeleton rows={6} cols={5} />
      ) : clients.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <Buildings size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No clients yet</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowCreate(true)}>Add your first client</Button>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b border-border">
                <SortHeader label="Company" k="company" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <SortHeader label="Primary Contact" k="primary" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <SortHeader label="Email" k="email" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <SortHeader label="Phone" k="phone" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <SortHeader label="Portal" k="portal" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <SortHeader label="Contacts" k="contacts" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} testidPrefix="clients-sort" />
                <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleClients.map(c => (
                <tr key={c.id} className={`border-b border-border hover:bg-muted/20 transition-colors cursor-pointer ${c._nested ? "bg-muted/10" : ""}`} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className={`flex items-center gap-2 ${c._nested ? "pl-6" : ""}`}>
                      {c._nested && <ArrowElbowDownRight size={14} className="text-muted-foreground shrink-0" />}
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${c.client_kind === "main" ? "bg-violet-500/15 text-violet-700 dark:text-violet-300" : "bg-primary/10 text-primary"}`}>
                        {(c.company_name || c.first_name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-1.5 flex-wrap">
                          {c.company_name || "—"}
                          {c.client_kind === "main" && <Badge className="text-[9px] uppercase tracking-wide bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-300 hover:bg-violet-500/10" data-testid={`client-kind-badge-${c.id}`}><TreeStructure size={10} className="mr-0.5" /> Main · {c.sub_client_count} sub-client{c.sub_client_count === 1 ? "" : "s"}</Badge>}
                          {c.client_kind === "sub" && <Badge variant="outline" className="text-[9px] uppercase tracking-wide" data-testid={`client-kind-badge-${c.id}`}>Sub of {c.parent_name}</Badge>}
                        </div>
                        {c.job_role && <p className="text-[10px] text-muted-foreground">{c.job_role}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium">{c.first_name} {c.last_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone || "—"}</td>
                  <td className="px-4 py-3">
                    {c.portal_enabled ? (
                      <Badge variant="default" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 hover:bg-emerald-100"><Globe size={10} className="mr-0.5" /> Active</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">Off</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="text-[10px]">{(c.contacts || []).length}</Badge></td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(c.id)} data-testid={`delete-client-${c.id}`}><Trash size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { if (!open) { setEditingClient(null); setForm({ ...EMPTY_FORM }); } setShowCreate(open); }}>
        <DialogContent className="max-w-lg" data-testid="client-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editingClient ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription>Primary contact details for this client.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required placeholder="John" data-testid="client-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required placeholder="Smith" data-testid="client-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Company Name</Label>
                <Input value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} placeholder="Smith Properties Ltd" data-testid="client-company-name" />
              </div>
              <div>
                <Label>Job Role</Label>
                <Input value={form.job_role} onChange={e => setForm({ ...form, job_role: e.target.value })} placeholder="Property Manager" data-testid="client-job-role" />
              </div>
            </div>
            <div>
              <Label>Company Address</Label>
              <AddressAutocomplete
                value={form.company_address}
                onChange={v => setForm({ ...form, company_address: v })}
                onSelect={(a) => setForm(f => ({
                  ...f,
                  company_address: a.formatted_address || f.company_address,
                  company_street: a.street_address || "",
                  company_town: a.town_city || "",
                  company_county: a.county || "",
                  company_postcode: a.postcode || "",
                  company_lat: a.latitude || null,
                  company_lng: a.longitude || null,
                }))}
                placeholder="42 High Street, London, SW1A 1AA"
                data-testid="client-company-address"
              />
              {form.company_street && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Parsed: {[form.company_street, form.company_town, form.company_county, form.company_postcode].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@smithproperties.co.uk" data-testid="client-email" />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+44 7911 123456" data-testid="client-phone" />
              </div>
            </div>
            {(!editingClient || editingClient.client_kind !== "main") && (
              <div>
                <Label>Part of a main client? <span className="text-muted-foreground font-normal">(white-label FM)</span></Label>
                <select value={form.parent_client_id} onChange={e => setForm({ ...form, parent_client_id: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="client-parent-select">
                  <option value="">No — standalone client</option>
                  {mainCandidates.map(m => <option key={m.id} value={m.id}>{m.company_name || `${m.first_name} ${m.last_name}`}{m.client_kind === "main" ? ` (main · ${m.sub_client_count})` : ""}</option>)}
                </select>
                <p className="text-[10px] text-muted-foreground mt-1">Sub-clients use the main client's branding everywhere, the main client's portal sees their work, and their invoices are billed to the main client.</p>
              </div>
            )}
            <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/20">
              <div>
                <Label className="text-sm font-medium">Client Portal Access</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Allow this client to view project updates via a portal link</p>
              </div>
              <Switch checked={form.portal_enabled} onCheckedChange={v => setForm({ ...form, portal_enabled: v })} data-testid="client-portal-toggle" />
            </div>
            {!form.parent_client_id && (
              <div className="flex items-center justify-between p-3 border border-border rounded-lg bg-muted/20" data-testid="client-form-po-required-row">
                <div>
                  <Label className="text-sm font-medium">PO / WO Required for Invoicing</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Jobs can't be invoiced until a client PO/WO number is entered (individual jobs can be waived)</p>
                </div>
                <Switch checked={form.po_required} onCheckedChange={v => setForm({ ...form, po_required: v })} data-testid="client-form-po-required-toggle" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="client-save-btn">{saving ? "Saving..." : editingClient ? "Update Client" : "Add Client"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
